import {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import "../../../modals/Modal.css";
import { Subscription } from "../../../../../api/subscriptions";
import { SubscriptionTier } from "../../../../../api/tiers";
import { SubscriptionsContext } from "../../../store/SubscriptionsContext";

/** Use with `form={TIER_FORM_ID}` on submit controls in the parent StepNavPanel footer. */
export const TIER_FORM_ID = "tier-form";

export type TierFormHandle = {
  getHasUnsavedChanges: () => boolean;
};

type TierFormProps = {
  onHandleSubmit: (tierData: TierFormData) => void;
  activeSub: Subscription;
  activeTier?: SubscriptionTier;
};

function baselineFingerprint(
  data: TierFormData,
  dependencySectionChecked: boolean,
  activeSub: Subscription,
): string {
  return JSON.stringify({
    name: data.name,
    price: data.price,
    oldCsaMemberPrice: data.oldCsaMemberPrice,
    spots: data.spots ?? null,
    dependsOn: data.dependsOn ?? null,
    takesFrom: [...(data.takesFrom ?? [])].sort(),
    dependencySectionChecked,
    isComboSub: activeSub.isCombo === true,
  });
}

const comboTakesFromValidationMessage =
  "At least 1 option needs to be checked regarding where the spots in this tier are coming from.";
export type TierFormData = {
  name: string;
  price: number;
  oldCsaMemberPrice: number;
  spots?: number;
  dependsOn?: string;
  takesFrom?: string[];
};

export const TierForm = forwardRef<TierFormHandle, TierFormProps>(
  function TierForm({ onHandleSubmit, activeSub, activeTier }, ref) {
    const { subscriptions, tiers } = useContext(SubscriptionsContext);
    const [isCombo, setIsCombo] = useState(false);
    const [isDependencyChecked, setIsDependencyChecked] = useState(false);

    const [tierData, setTierData] = useState<TierFormData>({
      name: "",
      price: 0,
      oldCsaMemberPrice: 0,
    });
    const [formError, setFormError] = useState<string | null>(null);
    const baselineRef = useRef("");
    const tiersInThisSubscription = tiers.filter(
      (tiers) => tiers.subscriptionId === activeSub.id,
    );
    const dependencyOptions = tiersInThisSubscription.map((tier) => {
      return (
        <option key={tier.id} value={tier.id}>
          {tier.name}
        </option>
      );
    });

    const filteredSubscriptionList = subscriptions.filter(
      (sub) => sub.isCombo === false || sub.isCombo === undefined,
    );
    const subscriptionOptions = filteredSubscriptionList.map((sub) => {
      return (
        <label key={sub.id}>
          <input
            type="checkbox"
            name="takes-spots-from"
            value={sub.id}
            checked={tierData?.takesFrom?.includes(sub.id)}
            onChange={(e) => handleCheckboxChange(e.target.checked, sub.id)}
            placeholder="Tier Name"
          />
          {sub.name}
        </label>
      );
    });

    function handleCheckboxChange(isChecked: boolean, id: string) {
      setFormError(null);
      if (isChecked) {
        setTierData((prev) => {
          const currentlyTakesFrom = prev?.takesFrom || [];
          return { ...prev, takesFrom: [...currentlyTakesFrom, id] };
        });

        return;
      }

      setTierData((prev) => {
        const currentlyTakesFrom = prev?.takesFrom || [];
        const listWithRemovedSub = currentlyTakesFrom.filter(
          (sub) => sub !== id,
        );
        return { ...prev, takesFrom: listWithRemovedSub };
      });
    }

    const handleInputChange = (field: string, value: any) => {
      setTierData((prev) => {
        const tierData: TierFormData = { ...prev };

        if (field === "name" || field === "dependsOn") {
          tierData[field] = value as string;
        }

        if (
          field === "price" ||
          field === "spots" ||
          field === "oldCsaMemberPrice"
        ) {
          tierData[field] = Number(value);
        }
        return tierData;
      });
    };

    useImperativeHandle(
      ref,
      () => ({
        getHasUnsavedChanges: () =>
          baselineFingerprint(tierData, isDependencyChecked, activeSub) !==
          baselineRef.current,
      }),
      [tierData, isDependencyChecked, activeSub],
    );

    useEffect(() => {
      if (activeSub.isCombo) {
        setIsCombo(true);
      } else {
        setIsCombo(false);
      }

      let initial: TierFormData;
      let depChecked: boolean;

      if (activeTier) {
        initial = {
          name: activeTier.name,
          price: activeTier.price,
          oldCsaMemberPrice: activeTier.oldCsaMemberPrice,
          spots: activeTier.spots,
          dependsOn: activeTier.dependsOn,
          takesFrom: activeTier.takesFrom,
        };
        depChecked = Boolean(activeTier.dependsOn);
      } else {
        initial = { name: "", price: 0, oldCsaMemberPrice: 0 };
        depChecked = false;
      }

      setTierData(initial);
      setIsDependencyChecked(depChecked);
      setFormError(null);
      baselineRef.current = baselineFingerprint(initial, depChecked, activeSub);
    }, [activeTier, activeSub]);

    return (
      <form
        id={TIER_FORM_ID}
        onSubmit={(e) => {
          e?.preventDefault();
          if (
            activeSub.isCombo &&
            (!tierData.takesFrom || tierData.takesFrom.length < 1)
          ) {
            setFormError(comboTakesFromValidationMessage);
            return;
          }
          setFormError(null);
          onHandleSubmit(tierData);
        }}
      >
        <div className="tier-form-inputs">
          <div className="modal-label-and-input-pair">
            <label htmlFor="tier-name" className="">
              Tier Name
            </label>
            <input
              type="text"
              name="tierName"
              id="tier-name"
              value={tierData?.name}
              placeholder="Tier Name"
              onChange={(e) => handleInputChange("name", e.target.value)}
              required
            />
          </div>{" "}
          <div className="modal-label-and-input-pair">
            <label htmlFor="tier-price" className="">
              Price
            </label>
            <input
              type="number"
              name="tierPrice"
              id="tier-price"
              placeholder="Price / Year"
              value={tierData?.price}
              onChange={(e) => handleInputChange("price", e.target.value)}
              required
              min={0}
            />
          </div>{" "}
          <div className="modal-label-and-input-pair">
            <label htmlFor="tier-old-csa-member-price" className="">
              Returning CSA member price
            </label>
            <input
              type="number"
              name="tierOldCsaMemberPrice"
              id="tier-old-csa-member-price"
              placeholder="Returning CSA member price / Year"
              value={tierData?.oldCsaMemberPrice}
              onChange={(e) =>
                handleInputChange("oldCsaMemberPrice", e.target.value)
              }
              required
              min={0}
            />
          </div>
        </div>
        {activeSub.isCombo !== true && (
          <div className="info-card tier-info-card">
            <label>
              <input
                type="checkbox"
                name="dependenciesCheckbox"
                checked={isDependencyChecked}
                onChange={() => setIsDependencyChecked((prev) => !prev)}
              />
              The amount of spots in this tier will depend on another tier.
            </label>

            {isDependencyChecked && (
              <div className="modal-form-input-pair">
                <label htmlFor="initial-spots" className="sr-only">
                  Initial Spots
                </label>
                <input
                  type="number"
                  name="initialSpots"
                  id="initial-spots"
                  placeholder="Initial Spots"
                  value={tierData?.spots}
                  onChange={(e) => handleInputChange("spots", e.target.value)}
                  required
                />
                <label htmlFor="selected-dependency" className="sr-only">
                  Selected Dependency
                </label>
                <select
                  className="dropdown dropdown-no-margins"
                  value={tierData?.dependsOn}
                  onChange={(e) =>
                    handleInputChange("dependsOn", e.target.value)
                  }
                >
                  {isDependencyChecked && !tierData.dependsOn && (
                    <option value="">Select Dependency</option>
                  )}
                  {dependencyOptions}
                </select>
              </div>
            )}
          </div>
        )}
        {isCombo && (
          <div className="info-card">
            <fieldset className="tier-checkbox-group">
              <legend>
                Which subscriptions does this tier take spots from?
              </legend>
              {subscriptionOptions}
            </fieldset>
          </div>
        )}
        {formError && (
          <p className="pickup-form-validation-msg" role="alert">
            {formError}
          </p>
        )}
      </form>
    );
  },
);
